import React, { useState } from "react";
import { toast } from "sonner";
import api, { OWNER_CONTACT, waLink, mailLink } from "../lib/api";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { MessageCircle, Phone, Mail, MapPin } from "lucide-react";

export default function Contact() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in name, email and message.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/inquiries", {
        name: form.name,
        email: form.email,
        phone: form.phone,
        message: form.message,
      });
      toast.success("Message sent — I'll reply within a few hours.");
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch (err) {
      toast.error("Could not send. Please try WhatsApp or email.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page" className="pt-32 pb-24">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
        <p className="overline mb-4">Get in touch</p>
        <h1 className="font-serif-luxe text-6xl md:text-8xl leading-none tracking-tighter max-w-4xl">
          Say hello. Any hour, <span className="italic text-[#D4AF37]">any question</span>.
        </h1>

        <div className="mt-20 grid grid-cols-1 lg:grid-cols-12 gap-16">
          {/* Direct methods */}
          <div className="lg:col-span-5 space-y-8">
            <ContactMethod
              icon={<MessageCircle size={18} />}
              label="WhatsApp"
              value="Fastest — usually within minutes"
              href={waLink("Hi! I'd love to book a stay.")}
              testid="contact-whatsapp"
              cta="Open WhatsApp"
            />
            <ContactMethod
              icon={<Phone size={18} />}
              label="Phone"
              value={OWNER_CONTACT.phone}
              href={`tel:+${OWNER_CONTACT.whatsapp}`}
              testid="contact-phone"
              cta="Call now"
            />
            <ContactMethod
              icon={<Mail size={18} />}
              label="Email"
              value={OWNER_CONTACT.email}
              href={mailLink("Inquiry via vacaystayrentals.com")}
              testid="contact-email"
              cta="Send email"
            />
            <div className="border-t border-[#F5F5DC]/10 pt-8">
              <p className="overline mb-3">Response time</p>
              <p className="text-[#F5F5DC]/80 leading-relaxed">
                I answer every inquiry personally, usually within a few hours.
                For urgent questions or same-week stays, WhatsApp is the
                fastest way to reach me.
              </p>
            </div>
          </div>

          {/* Form */}
          <form
            onSubmit={submit}
            data-testid="contact-form"
            className="lg:col-span-7 border border-[#F5F5DC]/10 bg-[#1C1C1E] p-8 md:p-12 space-y-6"
          >
            <div>
              <Label className="text-xs uppercase tracking-[0.2em] text-[#A3A3A3]">
                Full name
              </Label>
              <Input
                data-testid="contact-name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="mt-2 bg-[#121212] border-[#F5F5DC]/10 focus:border-[#D4AF37]"
                required
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="text-xs uppercase tracking-[0.2em] text-[#A3A3A3]">
                  Email
                </Label>
                <Input
                  data-testid="contact-email-input"
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="mt-2 bg-[#121212] border-[#F5F5DC]/10 focus:border-[#D4AF37]"
                  required
                />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-[0.2em] text-[#A3A3A3]">
                  Phone
                </Label>
                <Input
                  data-testid="contact-phone-input"
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  className="mt-2 bg-[#121212] border-[#F5F5DC]/10 focus:border-[#D4AF37]"
                />
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-[0.2em] text-[#A3A3A3]">
                Message
              </Label>
              <Textarea
                data-testid="contact-message"
                rows={6}
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                className="mt-2 bg-[#121212] border-[#F5F5DC]/10 focus:border-[#D4AF37]"
                required
                placeholder="Tell me about your trip — dates, party size, any preferences…"
              />
            </div>
            <Button
              type="submit"
              disabled={submitting}
              data-testid="contact-submit"
              className="w-full h-12 bg-[#D4AF37] text-[#121212] hover:bg-[#B3922E] text-xs tracking-[0.24em] uppercase rounded-none"
            >
              {submitting ? "Sending…" : "Send Message"}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}

function ContactMethod({ icon, label, value, href, testid, cta }) {
  return (
    <a
      href={href}
      target={href.startsWith("http") ? "_blank" : undefined}
      rel="noreferrer"
      data-testid={testid}
      className="group block border border-[#F5F5DC]/10 hover:border-[#D4AF37]/40 p-6 transition-all bg-[#1C1C1E]/40"
    >
      <div className="flex items-start gap-4">
        <div className="w-11 h-11 flex items-center justify-center border border-[#D4AF37]/40 text-[#D4AF37] shrink-0">
          {icon}
        </div>
        <div className="flex-1">
          <p className="overline">{label}</p>
          <p className="mt-1 text-[#F5F5DC]">{value}</p>
        </div>
        <span className="text-xs uppercase tracking-[0.2em] text-[#D4AF37] group-hover:translate-x-1 transition-transform">
          {cta} →
        </span>
      </div>
    </a>
  );
}
